import { authorizedFetch } from '../utils.js';
import { getReadModelFromItemType, itemTypesByReadModel } from './item-types';

export const searchItems = query =>
  authorizedFetch(`/api/search/eventflow*/_search`, 'POST', {
    query: {
      bool: {
        must: [
          {
            multi_match: {
              query: query,
              fields: ['id^4', 'name^3', 'categories^1.4', '*'],
              type: 'phrase_prefix',
              lenient: true
            }
          }
        ],
        must_not: [
          {
            exists: { field: 'dataDeliveries' } // data campaigns
          },
          {
            exists: { field: 'segment' } // audiences
          }
        ]
      }
    }
  });

export const searchItemsByType = ({
  itemTypes,
  additionalFilters,
  query = '',
  excludeIds = [],
  excludeArchived = false,
  sort = {},
  from = 0,
  size = 20
}) => {
  const readModels = itemTypes.map(itemType =>
    getReadModelFromItemType(itemType)
  );

  return new Promise((resolve, reject) => {
    const musts = [];
    if (additionalFilters) {
      if (Array.isArray(additionalFilters)) {
        musts.push(...additionalFilters);
      } else {
        //default behavior is a single object term filter
        musts.push({ term: additionalFilters });
      }
    }

    // Build the exclusion clauses. excludeArchived is a reports-scoped opt-in
    // (GRU-7701) and must not alter other grids' query bodies, so a single
    // exclusion keeps the original object shape and only multiple exclusions
    // become an array.
    const exclusions = [];
    if (excludeIds.length > 0) {
      exclusions.push({
        terms: {
          'id.keyword': excludeIds
        }
      });
    }
    if (excludeArchived) {
      exclusions.push({ term: { isArchived: true } });
    }

    let must_not;
    if (exclusions.length === 1) {
      must_not = exclusions[0];
    } else if (exclusions.length > 1) {
      must_not = exclusions;
    }

    const queryRequest = {
      bool: {
        must: musts,
        must_not
      }
    };

    if (query) {
      queryRequest.bool.filter = {
        multi_match: {
          query: query,
          type: 'phrase_prefix'
        }
      };
    }

    authorizedFetch(
      `/api/search/${readModels
        .map(rm => `eventflow-${rm}`)
        .join(',')}/_search`,
      'POST',
      {
        from,
        size,
        sort: sort?._script ? sort : [sort],
        query: queryRequest
      }
    )
      .then(response => {
        resolve({
          items: response.hits.hits.map(h => {
            const readModel = h._index.replace('eventflow-', '');
            return {
              type: itemTypesByReadModel[readModel],
              ...h._source
            };
          }),
          totalMatches: response.hits.total
        });
      })
      .catch(reject);
  });
};

// Given a grid's recent-history list ([{id, name}] from localStorage) for a
// grid that hides archived items, return only the entries that still exist
// non-archived and weren't excluded this session (e.g. a report the user just
// archived). Mirrors the grid's excludeArchived contract for the Recent list
// (GRU-7701). On no history, returns [] without hitting the server.
export const filterArchivedFromRecent = async ({
  itemTypes,
  history = [],
  excludeIds = []
}) => {
  if (history.length === 0) {
    return [];
  }
  const { items } = await searchItemsByType({
    itemTypes,
    additionalFilters: [{ terms: { 'id.keyword': history.map(i => i.id) } }],
    excludeArchived: true,
    size: history.length
  });
  const activeIds = new Set(items.map(i => i.id));
  const sessionExcluded = new Set(excludeIds);
  return history.filter(
    i => activeIds.has(i.id) && !sessionExcluded.has(i.id)
  );
};

export const fetchItemsByType = ({
  itemType,
  filters = [],
  query = '',
  orderByField = 'dateModified',
  orderByAsc,
  from = 0,
  size = 20
}) => {
  return new Promise((resolve, reject) => {
    // Get sort state from localStorage
    const storedSort = localStorage.getItem(`simpleTableSort_${itemType}`);
    let finalOrderByField = orderByField;
    let finalOrderByAsc = orderByAsc;

    if (storedSort) {
      try {
        const parsed = JSON.parse(storedSort);
        finalOrderByField = parsed.filter;
        finalOrderByAsc = parsed.order === 'asc';
      } catch (e) {
        console.warn('Failed to parse stored sort state:', e);
      }
    }

    authorizedFetch(`/api/${itemType}/search`, 'POST', {
      query,
      from,
      size,
      orderByField: finalOrderByField,
      orderByAsc: finalOrderByAsc,
      filters
    })
      .then(response => {
        resolve({
          items: response.items.map(item => {
            return {
              type: itemType,
              ...item
            };
          }),
          totalMatches: response.totalHits
        });
      })
      .catch(reject);
  });
};
